import type { HubConnection } from "@microsoft/signalr";

import { DEFAULT_SIGNALR_URL, getSignalRUrl, isSignalRDisabled } from "@/configs/environment.config";

export type ConnectionLifecycleState =
  | "connecting"
  | "connected"
  | "reconnecting"
  | "disconnected";

export { DEFAULT_SIGNALR_URL };

export interface SignalRClientOptions {
  url?: string;
  autoReconnect?: boolean;
  reconnectDelay?: number;
  accessTokenFactory?: () => string;
}

export class SignalRClient {
  private connection: HubConnection | null = null;
  private readonly url: string;
  private readonly autoReconnect: boolean;
  private readonly reconnectDelay: number;
  private readonly accessTokenFactory?: () => string;
  private connectionState: ConnectionLifecycleState = "disconnected";
  private handlers = new Map<string, Set<(...args: unknown[]) => void>>();
  private startedAt: number | null = null;

  constructor(options: SignalRClientOptions = {}) {
    this.url = options.url ?? getSignalRUrl();
    this.autoReconnect = options.autoReconnect ?? true;
    this.reconnectDelay = options.reconnectDelay ?? 2000;
    this.accessTokenFactory = options.accessTokenFactory;
  }

  getState(): ConnectionLifecycleState {
    return this.connectionState;
  }

  getStartedAt(): number | null {
    return this.startedAt;
  }

  getConnection(): HubConnection | null {
    return this.connection;
  }

  isConnected(): boolean {
    return this.connectionState === "connected";
  }

  on(eventName: string, handler: (...args: unknown[]) => void): () => void {
    const current = this.handlers.get(eventName) ?? new Set();
    current.add(handler);
    this.handlers.set(eventName, current);

    if (this.connection) {
      this.connection.on(eventName, (...args: unknown[]) => handler(...args));
    }

    return () => this.off(eventName, handler);
  }

  off(eventName: string, handler: (...args: unknown[]) => void): void {
    const current = this.handlers.get(eventName);

    if (!current) {
      return;
    }

    current.delete(handler);

    if (current.size === 0) {
      this.handlers.delete(eventName);
    }

    this.connection?.off(eventName, handler as never);
  }

  async start(): Promise<void> {
    if (typeof window === "undefined") {
      return;
    }

    if (isSignalRDisabled()) {
      this.connectionState = "disconnected";
      return;
    }

    if (this.connection && this.connection.state === "Connected") {
      this.connectionState = "connected";
      return;
    }

    try {
      const signalR = await import("@microsoft/signalr");

      const connection = new signalR.HubConnectionBuilder()
        .withUrl(this.url, {
          accessTokenFactory: this.accessTokenFactory,
          transport: signalR.HttpTransportType.WebSockets,
          withCredentials: false,
        })
        .withAutomaticReconnect(this.autoReconnect ? [0, 2000, 5000, 10000] : [])
        .build();

      this.connection = connection;
      this.connectionState = "connecting";
      this.startedAt = Date.now();

      this.attachLifecycleCallbacks(connection);
      this.attachRegisteredHandlers(connection);

    //   await connection.start();
      this.connectionState = "connected";
      this.startedAt = Date.now();
    } catch (error) {
      this.connectionState = "disconnected";
      if (this.autoReconnect) {
        try {
          await this.delay(this.reconnectDelay);
          await this.reconnect();
        } catch {
          // Ignore retry failure and keep app alive.
        }
      }
      console.error("SignalR connection failed", error);
    }
  }

  async stop(): Promise<void> {
    if (!this.connection) {
      this.connectionState = "disconnected";
      return;
    }

    this.connectionState = "disconnected";
    await this.connection.stop();
    this.connection = null;
  }

  async reconnect(): Promise<void> {
    this.connectionState = "reconnecting";
    await this.stop();
    await this.start();
  }

  async invoke(eventName: string, payload?: unknown): Promise<void> {
    if (!this.connection || this.connection.state !== "Connected") {
      return;
    }

    await this.connection.invoke(eventName, payload);
  }

  private attachLifecycleCallbacks(connection: HubConnection): void {
    connection.onreconnecting(() => {
      this.connectionState = "reconnecting";
    });

    connection.onreconnected(() => {
      this.connectionState = "connected";
      this.startedAt = Date.now();
    });

    connection.onclose(() => {
      this.connectionState = "disconnected";
    });
  }

  private attachRegisteredHandlers(connection: HubConnection): void {
    this.handlers.forEach((handlers, eventName) => {
      handlers.forEach((handler) => {
        connection.on(eventName, (...args: unknown[]) => handler(...args));
      });
    });
  }

  private delay(value: number): Promise<void> {
    return new Promise((resolve) => {
      window.setTimeout(resolve, value);
    });
  }
}

export const defaultSignalRClient = new SignalRClient();
